import React, { useEffect, useState } from "react";
import { FaClock, FaCheckCircle, FaInbox } from "react-icons/fa";
import { Card } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserInspectionData } from "../../redux/slice/DashboardSlice/DashboardSlice";
import { fetchJobSiteData } from "../../redux/slice/InspectionSlice/InspectionSlice";
import { fetchCompanyDataPopulate } from "../../redux/slice/CompanySlice/CompanySlice";
import ColumnsChart from "../../components/UserDashboard/columnChart";
import Loader from "../../components/Loader/Loader";
import IncomingPackagesTable from "../../components/UserDashboard/IncomingPackagesTable";

const UserDashboard = () => {
    const dispatch = useDispatch();

    const { UserInspectionData } = useSelector((state) => state?.Dashboard);
    const { JobSiteData, status } = useSelector((state) => state?.Inspections);
    const { CompanyDataPopulate } = useSelector((state) => state?.Companies);

    const [data, setData] = useState({});
    const [selectedCompany, setSelectedCompany] = useState("");
    const [selectedJobsite, setSelectedJobsite] = useState("");

    useEffect(() => {
        dispatch(fetchUserInspectionData());
        dispatch(fetchCompanyDataPopulate());
    }, [dispatch]);

    useEffect(() => {
        if (UserInspectionData) setData(UserInspectionData);
    }, [UserInspectionData]);

    // Set first company by default
    useEffect(() => {
        if (CompanyDataPopulate?.length > 0 && !selectedCompany) {
            const firstCompanyId = CompanyDataPopulate[0].id;
            setSelectedCompany(firstCompanyId);
        }
    }, [CompanyDataPopulate, selectedCompany]);

    // Fetch jobsites when company changes
    useEffect(() => {
        if (selectedCompany) {
            dispatch(fetchJobSiteData(selectedCompany));
        }
    }, [selectedCompany, dispatch]);

    // Set first jobsite by default once JobSiteData is loaded
    useEffect(() => {
        if (JobSiteData?.data?.JobSitesData?.length > 0 && !selectedJobsite) {
            const firstJobsiteId = JobSiteData.data.JobSitesData[0]._id;
            setSelectedJobsite(firstJobsiteId);
        }
    }, [JobSiteData, selectedJobsite]);

    const submissionCards = [
        { label: "Pending", value: data.pendingSubmission, color: "rgba(253, 209, 76, 0.43)", borderColor: "#ffc107", icon: <FaClock size={22} color="#ffc107" /> },
        { label: "Completed", value: data.completedSubmission, color: "rgba(107, 168, 120, 0.56)", borderColor: "#28a745", icon: <FaCheckCircle size={22} color="#28a745" /> },
        { label: "Incoming", value: data.incomingSubmission, color: "rgba(95, 154, 218, 0.58)", borderColor: "#007bff", icon: <FaInbox size={22} color="#007bff" /> },
    ];

    const approvalCards = [
        { label: "Pending", value: data.pendingApproval, color: "rgba(253, 209, 76, 0.43)", borderColor: "#ffc107", icon: <FaClock size={22} color="#ffc107" /> },
        { label: "Completed", value: data.completedApproval, color: "rgba(107, 168, 120, 0.56)", borderColor: "#28a745", icon: <FaCheckCircle size={22} color="#28a745" /> },
        { label: "Incoming", value: data.incomingApproval, color: "rgba(95, 154, 218, 0.58)", borderColor: "#007bff", icon: <FaInbox size={22} color="#007bff" /> },
    ];

    const renderCard = (card) => (
        <Card key={card.label} className="p-3 flex-fill d-flex justify-content-between align-items-center shadow-sm"
            style={{ backgroundColor: card.color, borderLeft: `4px solid ${card.borderColor}`, borderRadius: "16px", minWidth: "160px", flex: "1 1 30%" }}>
            <div className="d-flex justify-content-between align-items-center text-center w-100 ">
                <div className="flex-grow-1 text-white">
                    <h5 className="fw-bold mb-1 text-center">{card.value}</h5>
                    <small className=" text-center">{card.label}</small>
                    <div className="ms-2 mt-1">{card.icon}</div>
                </div>
            </div>
        </Card>
    );

    return (
        <div className="container-fluid pt-4 pb-4"
            style={{
                maxHeight: "100vh",
                overflowY: "auto",
                overflowX: "hidden",
            }}>
            {status === "loading" && <div className="text-center"><Loader animation="border" /></div>}
            <div className="row gy-4">
                <div className="col-12 col-lg-6">
                    <div className="dark-panel2 shadow rounded-4 p-3 h-100">
                        <h5 className="fw-semibold mb-3">Package Submissions</h5>
                        <div className="d-flex flex-wrap gap-3">{submissionCards.map(renderCard)}</div>
                    </div>
                </div>
                <div className="col-12 col-lg-6">
                    <div className=" dark-panel2 shadow rounded-4 p-3 h-100">
                        <h5 className="fw-semibold mb-3">Package Approvals</h5>
                        <div className="d-flex flex-wrap gap-3">{approvalCards.map(renderCard)}</div>
                    </div>
                </div>
            </div>

            {/* Jobsite Stats Chart */}
            <div className="row gy-4 mt-2">

                <div className="col-12 col-lg-6 mt-4">
                    <div className="dark-panel2 shadow rounded-4 p-3 h-100">
                        <div className="mb-3 row g-3 align-items-center ">
                            <div className="col-12 col-md-6">
                                <select className="form-select w-100" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)}>
                                    <option value="">Select Company</option>
                                    {CompanyDataPopulate?.map((comp) => (
                                        <option key={comp.id} value={comp.id}>{comp.companyName}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-12 col-md-6">
                                <select className="form-select w-100" value={selectedJobsite} onChange={(e) => setSelectedJobsite(e.target.value)} disabled={!selectedCompany}>
                                    <option value="">Select JobSite</option>
                                    {JobSiteData?.data?.JobSitesData?.map((site) => (
                                        <option key={site._id} value={site._id}>{site.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div style={{ minHeight: "235px" }} >
                            <ColumnsChart
                                data={
                                    data && selectedJobsite && data?.jobsiteWise.length > 0
                                        ? data?.jobsiteWise.find((js) => js.jobSiteId === selectedJobsite)
                                        : {}
                                }
                                jobSite={selectedJobsite}
                            />
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-12 col-lg-6 mt-4">
                    <div className="dark-panel2 shadow rounded-4 p-3 h-100 custom-grid2">
                        <h5 className="fw-semibold mb-3">Incoming Packages</h5>

                        {data?.incomingPackages && data.incomingPackages.length > 0 ? (
                            <IncomingPackagesTable data={data.incomingPackages} />
                        ) : (
                            <div className="text-center py-5">
                                No data available
                            </div>
                        )}
                    </div>
                </div>


            </div>

        </div>
    );
};

export default UserDashboard;
